import { Core } from "@faire/slate/foundation/color";
import { Star } from "@faire/web/slate/icons/foundation/Star";
import { Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { AccessibleStarsDescription } from "@faire/web/ui/AccessibleStarsDescription";
import range from "lodash/range";
import React, { useId } from "react";

export type IconSize = "compact" | "default" | "large" | "small";

interface IProps {
  rating: number;
  size?: IconSize;
}

const getFontSize = (iconSize: IconSize | undefined) => {
  if (iconSize === "compact" || iconSize === "small") {
    return "12px";
  } else if (iconSize === "large") {
    return "24px";
  } else {
    return "16px";
  }
};

export const RatingDisplay = (props: IProps) => {
  const linearGradientId = `partialStar-${useId()}`;
  const fontSize = getFontSize(props.size);
  const ratingOffset = `${(props.rating * 100) % 100}%`;
  const emptyColor =
    props.size === "compact" ? Core.text.subdued : Core.text.primaryInverse;

  return (
    <AccessibleStarsDescription rating={props.rating}>
      <Row>
        <svg
          style={{ width: 0, height: 0, visibility: "hidden" }}
          aria-hidden="true"
        >
          <defs>
            <linearGradient
              id={linearGradientId}
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop
                offset="0%"
                style={{ stopColor: Core.icon.primary, stopOpacity: 1 }}
              />
              <stop
                offset={ratingOffset}
                style={{ stopColor: Core.icon.primary, stopOpacity: 1 }}
              />
              <stop
                offset={ratingOffset}
                style={{
                  stopColor: emptyColor,
                  stopOpacity: 1,
                }}
              />
              <stop
                offset="100%"
                style={{
                  stopColor: emptyColor,
                  stopOpacity: 1,
                }}
              />
            </linearGradient>
          </defs>
        </svg>
        {range(1, 6).map((starValue, index) => (
          <React.Fragment key={index}>
            {index === 0 ? null : (
              <Spacer width={props.size === "compact" ? 2 : 4} />
            )}
            {/* This span gets ride of empty star border */}
            <span
              style={{ display: "flex" }}
              color={
                props.size === "compact" && starValue > props.rating
                  ? emptyColor
                  : Core.text.primary
              }
            >
              <Star
                fontSize={fontSize}
                fill={
                  // eslint-disable-next-line no-nested-ternary
                  starValue <= props.rating
                    ? Core.icon.primary
                    : starValue < props.rating + 1
                      ? `url(#${linearGradientId})`
                      : emptyColor
                }
              />
            </span>
          </React.Fragment>
        ))}
      </Row>
    </AccessibleStarsDescription>
  );
};
